import * as s from "@gromia/components/theme/t.module.css";
import { CloseButton } from "@gromia/components/ui/CloseButton";
import * as spriv from "@gromia/components/ui/Modal.module.css";
import { Actions, useModalState } from "@gromia/utils/useModalState";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as R from "fp-ts/lib/Record";
import React from "react";
import { Helmet } from "react-helmet";
import { Portal } from "react-portal";

export { useModalState } from "@gromia/utils/useModalState";
export type { Actions } from "@gromia/utils/useModalState";

type Child<T> = T | ((actions: Required<Actions>) => T);

export type SubProps = Actions & {
  noClose?: boolean;
  children: Child<NonNullable<React.ReactNode>>;
};

function children(props: SubProps) {
  return typeof props.children === "function"
    ? props.children({
      onModalClose: () => {},
      onModalOpen: () => {},
      onFullscreenToggle: () => {},
      ...props,
    })
    : props.children;
}

function Content(props: SubProps) {
  return (
    <div className={cx(spriv.content, s.px3, s.pb3, s.pt0, s.pxSm5, s.pbSm5)}>
      {children(props)}
    </div>
  );
}

type Ch<T> = T | T[];

type Props = {
  defaultIsOpen?: boolean;
  trigger: (a: Actions) => NonNullable<React.ReactNode>;
  children: Ch<React.ReactElement<React.ComponentProps<typeof Content>>>;
};

export function StaticModal(props: {
  noClose?: boolean;
  noClickOutClose?: boolean;
  mstat: ReturnType<typeof useModalState>;
  formProps?: { onSubmit: (e: React.FormEvent) => void };
  children: Props["children"];
  layout?: "narrow" | "default";
}) {
  const windowLayoutClass = pipe(
    props.layout,
    O.fromNullable,
    O.getOrElse<string>(() => "default"),
    x =>
      R.lookup(x)({
        narrow: spriv.windowLayoutNarrow,
      }),
    O.getOrElse(() => spriv.windowLayoutDefault),
  );

  const withFooter = React.Children.toArray(props.children).some(
    (x: any) => x.type === Footer,
  );

  const Container = props.formProps ? "form" : "div";

  const modal = (
    <Portal>
      <Helmet
        bodyAttributes={{
          class: spriv.htmlBody,
        }}
      />
      <div className={cx(spriv.overlay)} />
      <Container
        className={cx(
          spriv.container,
          props.mstat.isOpen ? s.opacity1 : s.opacity0,
        )}
        {...props.formProps}
      >
        <div
          ref={props.noClose || props.noClickOutClose
            ? null
            : (props.mstat.ref as any)}
          className={cx(
            spriv.window,
            windowLayoutClass,
            withFooter ? spriv.windowWithFooter : null,
          )}
        >
          <div className={cx(spriv.header)}>
            {props.noClose
              ? null
              : <CloseButton onClick={props.mstat.actions.onModalClose} />}
          </div>
          {React.Children.map(props.children, c =>
            React.cloneElement(c, {
              noClose: props.noClose,
              ...props.mstat.actions,
            }))}
        </div>
      </Container>
    </Portal>
  );

  return props.mstat.foldStateL(
    () => modal,
    () => null,
  );
}

export function Modal(
  props: Props & {
    noClose?: boolean;
    noClickOutClose?: boolean;
    formProps?: { onSubmit: (e: React.FormEvent) => void };
  },
) {
  const mstate = useModalState<HTMLElement>(props.defaultIsOpen);

  return (
    <React.Fragment>
      {props.trigger(mstate.actions)}
      <StaticModal
        mstat={mstate}
        children={props.children}
        noClose={props.noClose}
        noClickOutClose={props.noClickOutClose}
        formProps={props.formProps}
      />
    </React.Fragment>
  );
}

function Title(props: { children?: React.ReactNode }) {
  return (
    <span
      className={cx(
        s.dBlock,
        s.textAlignCenter,
        s.fontSizeLarge2,
        s.fontWeightBold,
      )}
    >
      {props.children}
    </span>
  );
}

function Text_(props: { children?: React.ReactNode }) {
  return (
    <span className={cx(s.dBlock, s.textAlignCenter, s.fontSizeBig, s.mt3)}>
      {props.children}
    </span>
  );
}

function Footer(props: { children?: React.ReactNode }) {
  return <div className={cx(spriv.footer)}>{props.children}</div>;
}

function FooterSpace(props: { children?: React.ReactNode }) {
  return <div className={cx(spriv.footerSpace)}>{props.children}</div>;
}

function FooterCenter(props: { children?: React.ReactNode }) {
  return <div className={cx(spriv.footerCenter)}>{props.children}</div>;
}

Modal.Content = Content;
Modal.Title = Title;
Modal.Footer = Footer;

StaticModal.Content = Content;
StaticModal.Title = Title;
StaticModal.Text = Text_;
StaticModal.Footer = Footer;
StaticModal.FooterSpace = FooterSpace;
StaticModal.FooterCenter = FooterCenter;
